import type { ILogicGraph } from '@/interfaces/models';
import type { NodeHandle } from './RuntimeHandles';

/** Numeric template bindings. Lift lanes have independent numeric maps. */
export class RuntimeBindings extends Map<NodeHandle, NodeHandle> {
  readonly lanes = new Map<number, Map<NodeHandle, NodeHandle>>();
  readonly #importedKeys = new Map<NodeHandle, string>();
  readonly #importedLaneKeys = new Map<number, Map<NodeHandle, string>>();

  /** Pure eligibility for snapshot observation, including ordinary nonempty maps.
   * Do not evaluate custom size/serialization getters to decide how to observe. */
  static hasNativeSnapshotHooks(value: unknown): value is RuntimeBindings {
    if (
      !value ||
      typeof value !== 'object' ||
      !(#importedKeys in value) ||
      Object.getPrototypeOf(value) !== RuntimeBindings.prototype
    )
      return false;
    if (
      Object.getPrototypeOf(RuntimeBindings.prototype) !== Map.prototype ||
      Object.hasOwn(value, 'constructor') ||
      Object.hasOwn(value, 'totalSize') ||
      Object.hasOwn(value, 'size') ||
      Object.hasOwn(value, 'toDto') ||
      Object.hasOwn(value, 'values') ||
      Object.hasOwn(value, Symbol.iterator) ||
      Object.hasOwn(RuntimeBindings.prototype, 'size') ||
      Object.hasOwn(RuntimeBindings.prototype, Symbol.iterator) ||
      Object.getOwnPropertyDescriptor(RuntimeBindings.prototype, 'totalSize')?.get !==
        nativeTotalSize ||
      Object.getOwnPropertyDescriptor(RuntimeBindings.prototype, 'toDto')?.value !==
        nativeBindingsToDto ||
      Object.getOwnPropertyDescriptor(Map.prototype, 'size')?.get !== nativeMapSize ||
      Object.getOwnPropertyDescriptor(Map.prototype, 'values')?.value !==
        nativeMapValues ||
      Object.getOwnPropertyDescriptor(Map.prototype, Symbol.iterator)?.value !==
        nativeMapIterator
    )
      return false;
    const lanes = Object.getOwnPropertyDescriptor(value, 'lanes');
    if (!lanes || !('value' in lanes)) return false;
    const plainMap = (map: unknown): map is Map<unknown, unknown> => {
      try {
        nativeMapSize.call(map);
        return (
          Object.getPrototypeOf(map) === Map.prototype &&
          !Object.hasOwn(map as object, 'size') &&
          !Object.hasOwn(map as object, 'values') &&
          !Object.hasOwn(map as object, Symbol.iterator)
        );
      } catch {
        return false;
      }
    };
    if (!plainMap(lanes.value)) return false;
    for (const lane of nativeMapValues.call(lanes.value))
      if (!plainMap(lane)) return false;
    return true;
  }

  static isNativeEmpty(value: object): boolean {
    if (
      !value ||
      typeof value !== 'object' ||
      !(#importedKeys in value) ||
      Object.getPrototypeOf(value) !== RuntimeBindings.prototype
    )
      return false;
    if (
      Object.getPrototypeOf(RuntimeBindings.prototype) !== Map.prototype ||
      Object.hasOwn(RuntimeBindings.prototype, 'size') ||
      Object.hasOwn(RuntimeBindings.prototype, Symbol.iterator) ||
      Object.hasOwn(value, Symbol.iterator) ||
      Object.hasOwn(value, 'totalSize') ||
      Object.hasOwn(value, 'size') ||
      Object.hasOwn(value, 'toDto') ||
      Object.getOwnPropertyDescriptor(RuntimeBindings.prototype, 'totalSize')?.get !==
        nativeTotalSize ||
      Object.getOwnPropertyDescriptor(RuntimeBindings.prototype, 'toDto')?.value !==
        nativeBindingsToDto ||
      Object.getOwnPropertyDescriptor(Map.prototype, 'size')?.get !== nativeMapSize ||
      Object.getOwnPropertyDescriptor(Map.prototype, 'values')?.value !==
        nativeMapValues ||
      Object.getOwnPropertyDescriptor(Map.prototype, Symbol.iterator)?.value !==
        nativeMapIterator
    )
      return false;
    const lanes = Object.getOwnPropertyDescriptor(value, 'lanes');
    if (!lanes || !('value' in lanes)) return false;
    try {
      return (
        nativeMapSize.call(value) === 0 &&
        nativeMapSize.call(lanes.value) === 0 &&
        Object.getPrototypeOf(lanes.value) === Map.prototype &&
        !Object.hasOwn(lanes.value, 'size') &&
        !Object.hasOwn(lanes.value, 'values') &&
        !Object.hasOwn(lanes.value, Symbol.iterator)
      );
    } catch {
      return false;
    }
  }

  get totalSize(): number {
    let count = this.size;
    for (const lane of this.lanes.values()) count += lane.size;
    return count;
  }

  static fromDto(
    values: Record<string, string> | undefined,
    graph: ILogicGraph,
    width?: number,
  ): RuntimeBindings {
    const bindings = new RuntimeBindings();
    if (!values) return bindings;
    const nodes = new Map(
      [...graph.nodes.values()].map((node) => [node.idText, node.id]),
    );
    for (const [key, externalId] of Object.entries(values)) {
      const value = nodes.get(externalId);
      if (value === undefined)
        throw new TypeError('Saved instance binding has no runtime node');
      const match = width ? /^(0|[1-9]\d*):(.*)$/s.exec(key) : null;
      const lane = match ? Number(match[1]) : -1;
      const template = graph.ids.scope.allocateNode();
      if (match && Number.isSafeInteger(lane) && lane >= 0 && lane < width!) {
        const entries = bindings.lanes.get(lane) ?? new Map<NodeHandle, NodeHandle>();
        const keys =
          bindings.#importedLaneKeys.get(lane) ?? new Map<NodeHandle, string>();
        entries.set(template, value);
        keys.set(template, match[2]);
        bindings.lanes.set(lane, entries);
        bindings.#importedLaneKeys.set(lane, keys);
      } else {
        bindings.set(template, value);
        bindings.#importedKeys.set(template, key);
      }
    }
    return bindings;
  }

  /** Resolve imported boundary keys once the actual template is available. */
  resolveTemplate(template: ILogicGraph | null | undefined): void {
    if (!template || (!this.#importedKeys.size && !this.#importedLaneKeys.size)) return;
    const nodes = new Map(
      [...template.nodes.values()].map((node) => [node.idText, node.id]),
    );
    const resolve = (
      entries: Map<NodeHandle, NodeHandle>,
      keys: Map<NodeHandle, string>,
    ) => {
      for (const [placeholder, text] of keys) {
        const handle = nodes.get(text);
        if (handle === undefined) continue;
        const value = entries.get(placeholder);
        entries.delete(placeholder);
        keys.delete(placeholder);
        if (value !== undefined) entries.set(handle, value);
      }
    };
    resolve(this, this.#importedKeys);
    for (const [lane, keys] of this.#importedLaneKeys) {
      resolve(this.lanes.get(lane)!, keys);
      if (!keys.size) this.#importedLaneKeys.delete(lane);
    }
  }

  toDto(
    graph: ILogicGraph,
    template?: ILogicGraph | null,
  ): Record<string, string> | undefined {
    if (!this.totalSize) return undefined;
    const result: Record<string, string> = {};
    const put = (key: string, id: NodeHandle) =>
      Object.defineProperty(result, key, {
        value: graph.nodeIdText(id),
        enumerable: true,
        writable: true,
        configurable: true,
      });
    for (const [key, id] of this)
      put(
        this.#importedKeys.get(key) ??
          template?.nodeIdText(key) ??
          graph.ids.nodeText(key),
        id,
      );
    for (const [lane, entries] of this.lanes)
      for (const [key, id] of entries)
        put(
          `${lane}:${this.#importedLaneKeys.get(lane)?.get(key) ?? template?.nodeIdText(key) ?? graph.ids.nodeText(key)}`,
          id,
        );
    return result;
  }
}

const nativeMapSize = Object.getOwnPropertyDescriptor(Map.prototype, 'size')!.get!;
const nativeMapValues = Map.prototype.values;
const nativeMapIterator = Map.prototype[Symbol.iterator];
const nativeTotalSize = Object.getOwnPropertyDescriptor(
  RuntimeBindings.prototype,
  'totalSize',
)!.get!;
const nativeBindingsToDto = RuntimeBindings.prototype.toDto;
